import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as api from '../api';
import { getErrorMessage } from '@/lib/api-client';

// Query keys
export const didKeys = {
  all: ['dids'] as const,
  lists: () => [...didKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...didKeys.lists(), filters] as const,
  details: () => [...didKeys.all, 'detail'] as const,
  detail: (tn: string) => [...didKeys.details(), tn] as const,
  metrics: () => [...didKeys.all, 'metrics'] as const,
  e911: (tn: string) => [...didKeys.all, 'e911', tn] as const,
  e911Alerts: (tn: string) => [...didKeys.all, 'e911-alerts', tn] as const,
};

// Order-related query keys
export const orderKeys = {
  endpointGroups: ['order', 'endpoint-groups'] as const,
  didGroups: ['order', 'did-groups'] as const,
};

// Port order query keys
export const portKeys = {
  all: ['ports'] as const,
  lists: () => [...portKeys.all, 'list'] as const,
  details: () => [...portKeys.all, 'detail'] as const,
  detail: (portId: number) => [...portKeys.details(), portId] as const,
};

// TelviaIQ order (activity log) query keys
export const telviaiqOrderKeys = {
  all: ['telviaiq-orders'] as const,
  lists: () => [...telviaiqOrderKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...telviaiqOrderKeys.lists(), filters] as const,
};

// CDR query keys
export const cdrKeys = {
  all: ['cdrs'] as const,
  mtd: (year?: number, month?: number) => [...cdrKeys.all, 'mtd', year, month] as const,
  trend: (months: number) => [...cdrKeys.all, 'trend', months] as const,
  daily: (days: number) => [...cdrKeys.all, 'daily', days] as const,
  list: (filters: Record<string, unknown>) => [...cdrKeys.all, 'list', filters] as const,
  perDid: (year?: number, month?: number, limit?: number) => [...cdrKeys.all, 'per-did', year, month, limit] as const,
  topInbound: (year?: number, month?: number, limit?: number) => [...cdrKeys.all, 'top-inbound', year, month, limit] as const,
  topOutbound: (year?: number, month?: number, limit?: number) => [...cdrKeys.all, 'top-outbound', year, month, limit] as const,
  utilization: () => [...cdrKeys.all, 'utilization'] as const,
};

/**
 * Hook to fetch all DIDs
 */
export function useDIDs() {
  return useQuery({
    queryKey: didKeys.lists(),
    queryFn: api.getDIDs,
  });
}

/**
 * Hook to fetch a single DID
 */
export function useDID(tn: string | null) {
  return useQuery({
    queryKey: didKeys.detail(tn || ''),
    queryFn: () => api.getDID(tn!),
    enabled: !!tn,
  });
}

/**
 * Hook to fetch DID metrics
 */
export function useDIDMetrics() {
  return useQuery({
    queryKey: didKeys.metrics(),
    queryFn: api.getDIDMetrics,
  });
}

/**
 * Hook to update CNAM setting
 */
export function useUpdateCNAM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.updateCNAM,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: didKeys.lists() });
      queryClient.setQueryData(didKeys.detail(data.tn), data);
      toast.success('CNAM setting updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to update call forwarding
 */
export function useUpdateForward() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.updateForward,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: didKeys.lists() });
      queryClient.invalidateQueries({ queryKey: didKeys.detail(variables.tn) });
      // Use API response 'enabled' field which reflects what was requested
      toast.success(data.enabled ? 'Forwarding enabled' : 'Forwarding disabled');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to update failover
 */
export function useUpdateFailover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.updateFailover,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: didKeys.lists() });
      queryClient.invalidateQueries({ queryKey: didKeys.detail(variables.tn) });
      // Use API response 'enabled' field which reflects what was requested
      toast.success(data.enabled ? 'Failover enabled' : 'Failover disabled');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to update conference line
 *
 * Note: Disabling conference removes ALL routing from the DID.
 * The backend automatically restores the original endpoint group.
 */
export function useUpdateConference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.updateConference,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: didKeys.lists() });
      queryClient.invalidateQueries({ queryKey: didKeys.detail(variables.tn) });
      // Use API response 'enabled' field which reflects what was requested
      toast.success(data.enabled ? 'Conference line enabled' : 'Conference disabled');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to update fax-to-email
 */
export function useUpdateFaxToEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.updateFaxToEmail,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: didKeys.lists() });
      queryClient.invalidateQueries({ queryKey: didKeys.detail(variables.tn) });
      // Use API response 'enabled' field which reflects what was requested
      toast.success(data.enabled ? 'eFax enabled' : 'eFax disabled');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to fetch 911 address
 */
export function use911Address(tn: string | null) {
  return useQuery({
    queryKey: didKeys.e911(tn || ''),
    queryFn: () => api.get911Address(tn!),
    enabled: !!tn,
  });
}

/**
 * Hook to update 911 address
 */
export function useUpdate911Address() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.update911Address,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: didKeys.lists() });
      queryClient.setQueryData(didKeys.e911(variables.tn), data);
      toast.success('911 address updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to remove 911 address
 */
export function useRemove911Address() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.remove911Address,
    onSuccess: (_, tn) => {
      queryClient.invalidateQueries({ queryKey: didKeys.lists() });
      queryClient.setQueryData(didKeys.e911(tn), null);
      toast.success('911 address removed');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to fetch 911 alerts
 */
export function use911Alerts(tn: string | null) {
  return useQuery({
    queryKey: didKeys.e911Alerts(tn || ''),
    queryFn: () => api.get911Alerts(tn!),
    enabled: !!tn,
  });
}

/**
 * Hook to add 911 alert
 */
export function useAdd911Alert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tn, email }: { tn: string; email: string }) =>
      api.add911Alert(tn, email),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: didKeys.e911Alerts(variables.tn) });
      toast.success('Alert email added');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to remove 911 alert
 */
export function useRemove911Alert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tn, email }: { tn: string; email: string }) =>
      api.remove911Alert(tn, email),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: didKeys.e911Alerts(variables.tn) });
      toast.success('Alert email removed');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to update Caller ID
 */
export function useUpdateCallerID() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.updateCallerID,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: didKeys.lists() });
      queryClient.setQueryData(didKeys.detail(data.tn), data);
      toast.success('Caller ID updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to disconnect (release) a DID
 */
export function useReleaseDID() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.releaseDID,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: didKeys.lists() });
      queryClient.invalidateQueries({ queryKey: didKeys.metrics() });
      toast.success('DID disconnected');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to fetch endpoint groups for ordering - cached for entire session
 */
export function useEndpointGroups() {
  return useQuery({
    queryKey: orderKeys.endpointGroups,
    queryFn: api.getOrderEndpointGroups,
    staleTime: Infinity,  // Never refetch during session
    gcTime: Infinity,     // Never garbage collect
  });
}

/**
 * Hook to fetch DID groups for ordering - cached for entire session
 */
export function useDIDGroups() {
  return useQuery({
    queryKey: orderKeys.didGroups,
    queryFn: api.getOrderDIDGroups,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

// =============================================================================
// Port Order Hooks
// =============================================================================

/**
 * Hook to fetch active port orders
 */
export function usePortOrders() {
  return useQuery({
    queryKey: portKeys.lists(),
    queryFn: api.getPortOrders,
    staleTime: 60000, // Consider stale after 1 minute
  });
}

/**
 * Hook to fetch a single port order
 */
export function usePortOrder(portId: number | null) {
  return useQuery({
    queryKey: portKeys.detail(portId || 0),
    queryFn: () => api.getPortOrder(portId!),
    enabled: !!portId,
  });
}

/**
 * Hook to check number portability
 */
export function useCheckPortability() {
  return useMutation({
    mutationFn: api.checkPortability,
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to submit a port order request via email
 */
export function useSubmitPortOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.submitPortOrder,
    onSuccess: () => {
      // Invalidate port orders list
      queryClient.invalidateQueries({ queryKey: portKeys.lists() });
      toast.success('Port request submitted! Our team will contact you within 1 business day.');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to submit a port order request with e-signature
 */
export function useSubmitPortOrderWithSignature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.submitPortOrderWithSignature,
    onSuccess: () => {
      // Invalidate port orders and activity log
      queryClient.invalidateQueries({ queryKey: portKeys.lists() });
      queryClient.invalidateQueries({ queryKey: telviaiqOrderKeys.lists() });
      toast.success('Port request submitted with signed LOA! Our team will contact you within 1 business day.');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Hook to fetch TelviaIQ orders (activity log)
 */
export function useTelviaIQOrders(filters: api.TelviaIQOrderFilters = {}) {
  return useQuery({
    queryKey: telviaiqOrderKeys.list(filters as Record<string, unknown>),
    queryFn: () => api.getTelviaIQOrders(filters),
    staleTime: 60000, // 1 minute
  });
}

// =============================================================================
// CDR Hooks
// =============================================================================

/**
 * Hook to fetch MTD CDR summary
 */
export function useCDRMTD(year?: number, month?: number) {
  return useQuery({
    queryKey: cdrKeys.mtd(year, month),
    queryFn: () => api.getCDRMTDSummary(year, month),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to fetch CDR monthly trend
 */
export function useCDRTrend(months = 12) {
  return useQuery({
    queryKey: cdrKeys.trend(months),
    queryFn: () => api.getCDRTrend(months),
    staleTime: 300000, // 5 minutes
  });
}

/**
 * Hook to fetch paginated CDR list
 */
export function useCDRList(params: api.CDRListParams) {
  return useQuery({
    queryKey: cdrKeys.list(params as Record<string, unknown>),
    queryFn: () => api.getCDRList(params),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to fetch per-DID CDR summary
 */
export function useCDRPerDID(year?: number, month?: number, limit = 20) {
  return useQuery({
    queryKey: cdrKeys.perDid(year, month, limit),
    queryFn: () => api.getCDRPerDID(year, month, limit),
    staleTime: 300000, // 5 minutes
  });
}

/**
 * Hook to fetch top inbound DIDs (ORIG calls grouped by DNIS)
 */
export function useCDRTopInbound(year?: number, month?: number, limit = 10) {
  return useQuery({
    queryKey: cdrKeys.topInbound(year, month, limit),
    queryFn: () => api.getCDRTopInbound(year, month, limit),
    staleTime: 300000, // 5 minutes
  });
}

/**
 * Hook to fetch top outbound DIDs (TERM calls grouped by ANI)
 */
export function useCDRTopOutbound(year?: number, month?: number, limit = 10) {
  return useQuery({
    queryKey: cdrKeys.topOutbound(year, month, limit),
    queryFn: () => api.getCDRTopOutbound(year, month, limit),
    staleTime: 300000, // 5 minutes
  });
}

/**
 * Hook to fetch daily CDR trend (for Daily chart view)
 */
export function useCDRDailyTrend(days = 7) {
  return useQuery({
    queryKey: cdrKeys.daily(days),
    queryFn: () => api.getCDRDailyTrend(days),
    staleTime: 300000, // 5 minutes
  });
}

/**
 * Hook to fetch DID utilization (DIDs with/without traffic in 30 days)
 */
export function useDIDUtilization() {
  return useQuery({
    queryKey: cdrKeys.utilization(),
    queryFn: api.getDIDUtilization,
    staleTime: 300000, // 5 minutes
  });
}

// Stale DIDs query keys
export const staleDIDKeys = {
  all: ['stale-dids'] as const,
  count: (days: number) => [...staleDIDKeys.all, 'count', days] as const,
  list: (days: number) => [...staleDIDKeys.all, 'list', days] as const,
};

/**
 * Hook to fetch stale DIDs count (from pre-aggregated usage summaries)
 */
export function useStaleDIDsCount(days = 30) {
  return useQuery({
    queryKey: staleDIDKeys.count(days),
    queryFn: () => api.getStaleDIDs(days, false),
    staleTime: 300000, // 5 minutes
  });
}

/**
 * Hook to fetch stale DIDs list (from pre-aggregated usage summaries)
 */
export function useStaleDIDsList(days = 30, enabled = false) {
  return useQuery({
    queryKey: staleDIDKeys.list(days),
    queryFn: () => api.getStaleDIDs(days, true),
    staleTime: 300000, // 5 minutes
    enabled,
  });
}
